/**
 * IndexTAPage — TA view for Indian indices (Nifty, Sensex, Bank Nifty, sectorals).
 *
 * Reuses ChartPanel v2 via the `resource="index"` escape hatch. A dropdown
 * lets the user flip between benchmark, broad and sectoral indices — all
 * backed by ``india_index_snapshot``. A market-breadth strip sits on top,
 * hydrated from ``/api/india/breadth``.
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, Loader2, Activity,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import PageHeader from '../../../components/layout/PageHeader';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';
import ChartPanel from '../../../components/charts/ChartPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

export default function IndexTAPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const wantBreadth = searchParams.get('breadth') === '1';

  const [indices, setIndices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(params.symbol || 'NIFTY_50');

  // Load index list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/indices`);
        if (!cancelled) setIndices(data.indices || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (params.symbol) setActive(params.symbol.toUpperCase());
  }, [params.symbol]);

  const activeIndex = useMemo(
    () => indices.find((i) => i.symbol === active),
    [indices, active],
  );

  const groups = useMemo(() => {
    const by = { benchmark: [], broad: [], sectoral: [] };
    for (const i of indices) {
      (by[i.category] || by.sectoral).push(i);
    }
    return by;
  }, [indices]);

  const selectIdx = (sym) => {
    setActive(sym);
    navigate(`/public/index-ta/${sym}`, { replace: true });
  };

  return (
    <div className="space-y-4">
      <SectionNav tabs={researchTabs} />
      <PageHeader
        title="Indices · Technical View"
        description="Nifty, Sensex, Bank Nifty and sectoral Nifty indices with candle charts + full indicator stack."
      />

      {/* Breadth strip (always visible; highlighted if ?breadth=1) */}
      <BreadthStrip highlighted={wantBreadth} />

      {/* Selector */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          <IndexGroupPicker title="Benchmarks" items={groups.benchmark} active={active} onSelect={selectIdx} />
          {groups.broad.length > 0 && (
            <IndexGroupPicker title="Broad market" items={groups.broad} active={active} onSelect={selectIdx} />
          )}
          {groups.sectoral.length > 0 && (
            <IndexGroupPicker title="Sectoral" items={groups.sectoral} active={active} onSelect={selectIdx} />
          )}
        </div>
      )}

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-500" />
            {activeIndex?.display_name || active}
            {activeIndex?.close != null && (
              <span className="text-sm text-muted-foreground font-normal">
                · {activeIndex.close.toFixed(2)}
                {activeIndex.change_pct != null && (
                  <span className={activeIndex.change_pct >= 0 ? 'text-emerald-500 ml-2' : 'text-red-500 ml-2'}>
                    {activeIndex.change_pct >= 0 ? '+' : ''}{activeIndex.change_pct.toFixed(2)}%
                  </span>
                )}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartPanel isin={active} resource="index" height={480} />
        </CardContent>
      </Card>
    </div>
  );
}


function IndexGroupPicker({ title, items, active, onSelect }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((i) => {
          const up = (i.change_pct || 0) >= 0;
          const isActive = active === i.symbol;
          const Icon = i.change_pct == null ? Minus : up ? TrendingUp : TrendingDown;
          return (
            <button
              key={i.symbol}
              onClick={() => onSelect(i.symbol)}
              className={`flex items-center gap-2 rounded-md border px-2 py-1 text-[11px] transition-colors ${
                isActive
                  ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-500'
                  : 'border-border hover:bg-muted/30'
              }`}
            >
              <Icon className={`w-3 h-3 ${i.change_pct == null ? '' : up ? 'text-emerald-500' : 'text-red-500'}`} />
              <span className="font-medium">{i.display_name}</span>
              {i.change_pct != null && (
                <span className={up ? 'text-emerald-500' : 'text-red-500'}>
                  {up ? '+' : ''}{i.change_pct.toFixed(2)}%
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}


function BreadthStrip({ highlighted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/breadth`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  if (loading) {
    return <div className="h-10 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;
  }
  if (!data || !data.series || !data.series.length) {
    return null;
  }
  const latest = data.series[0];
  const cells = [
    { label: 'Advance / Decline', value: latest.ad_ratio != null ? latest.ad_ratio.toFixed(2) : '—' },
    { label: 'Advancers',         value: latest.advancers ?? '—' },
    { label: 'Decliners',         value: latest.decliners ?? '—' },
    { label: '% above 50 DMA',    value: latest.pct_above_50dma != null ? `${latest.pct_above_50dma.toFixed(1)}%` : '—' },
    { label: '% above 200 DMA',   value: latest.pct_above_200dma != null ? `${latest.pct_above_200dma.toFixed(1)}%` : '—' },
    { label: 'New 52w Highs',     value: latest.new_52w_highs ?? '—' },
    { label: 'New 52w Lows',      value: latest.new_52w_lows ?? '—' },
  ];
  return (
    <Card className={highlighted ? 'border-emerald-500/40' : ''}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
          Market breadth · {latest.date}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {cells.map((c) => (
            <div key={c.label}>
              <div className="text-[10px] text-muted-foreground">{c.label}</div>
              <div className="text-sm font-semibold">{c.value}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
